import axios from "axios";
import Vue from "vue";
import VueAxios from "vue-axios";
import UtilService from "../services/util.service";

const DEFAULT_AUTH_PATH_PREFIX = "/auth";
const CREDENTIALS_INVALID_CODE = "AUTH_CREDENTIALS_INVALID";
const CREDENTIALS_EXPIRED_CODE = "AUTH_CREDENTIALS_EXPIRED";

/**
 * Cookie-based API service.
 *
 * - Sends credentials on every request (cookie auth)
 * - Routes auth endpoints to a dedicated auth microservice (optional)
 * - Automatically refreshes auth on 401 and retries once
 * - Redirects to /accessDenied on 403
 */
const ApiService = {
  store: null,
  programGroupKey: null,
  clientKey: null,
  appKey: null,
  baseUrl: null,
  authBaseUrl: null,
  authPathPrefix: DEFAULT_AUTH_PATH_PREFIX,
  refreshPath: null,
  httpRedirects: null,
  env: null,

  apiAxios: null,
  authAxios: null,
  _refreshPromise: null,

  normalizeLinkUrl(href) {
    if (!href) {
      return href;
    }

    // Strip protocol + host from absolute URLs before extracting the path,
    // so that a hostname like "api.example.com" doesn't produce a false match
    // for "/api" before the actual path component (e.g. indexOf("/api") on
    // "http://api.example.com/api/..." would hit index 6, not the path).
    const normalizedHref = href.trim().replace(/^https?:\/\/[^/]+/, "");
    const apiIndex = normalizedHref.indexOf("/api");
    if (apiIndex > -1) {
      return normalizedHref.substring(apiIndex);
    }

    return normalizedHref;
  },

  init(opts) {
    if (opts.store) {
      this.store = opts.store;
    }
    if (opts.programGroupKey) {
      this.programGroupKey = opts.programGroupKey;
    }
    if (opts.clientKey) {
      this.clientKey = opts.clientKey;
    }
    this.appKey = opts.appKey || (opts.env && opts.env.VUE_APP_APP_KEY) || null;

    this.env = opts.env || null;

    if (opts.baseUrl) {
      this.baseUrl = opts.baseUrl;
    } else {
      this.baseUrl = window.location.origin;
    }

    this.authBaseUrl = opts.authBaseUrl || (opts.env && opts.env.VUE_APP_AUTH_API_HOST) || this.baseUrl;
    this.authPathPrefix = opts.authPathPrefix || DEFAULT_AUTH_PATH_PREFIX;
    this.refreshPath = opts.refreshPath || this._joinPath(this.authPathPrefix, "refresh");

    if (opts.httpRedirects) {
      this.httpRedirects = opts.httpRedirects;
    }

    this.apiAxios = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true
    });
    this.authAxios = axios.create({
      baseURL: this.authBaseUrl,
      withCredentials: true
    });

    if (this.appKey) {
      this.apiAxios.defaults.headers.common["X-Gapp-App-Key"] = this.appKey;
      this.authAxios.defaults.headers.common["X-Gapp-App-Key"] = this.appKey;
    }

    Vue.use(VueAxios, this.apiAxios);
    Vue.axios = this.apiAxios;

    this._installInterceptors(this.apiAxios, "api");
    this._installInterceptors(this.authAxios, "auth");
  },

  _installInterceptors(instance, kind) {
    instance.interceptors.request.use(
      config => {
        config.__apiServiceKind = kind;
        return config;
      },
      error => Promise.reject(error)
    );

    instance.interceptors.response.use(
      response => response,
      error => this._handleResponseError(error, instance)
    );
  },

  async _handleResponseError(error, instance) {
    const status = error && error.response ? error.response.status : null;

    if (status === 403) {
      this._redirectForStatus(403);
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    const config = error ? error.config : null;
    if (!config) {
      this._redirectForStatus(401);
      return Promise.reject(error);
    }

    if (this._isLoginRequest(config)) {
      return Promise.reject(error);
    }

    if (config.__skipAuthRefresh) {
      this._redirectForStatus(401);
      return Promise.reject(error);
    }

    if (config._retry) {
      this._redirectForStatus(401);
      return Promise.reject(error);
    }

    if (this._isRefreshRequest(config)) {
      this._redirectForStatus(401);
      return Promise.reject(error);
    }

    config._retry = true;

    try {
      await this._refreshAuth();
      return instance.request(config);
    } catch (refreshError) {
      this._redirectForStatus(401);
      return Promise.reject(error);
    }
  },

  _isRefreshRequest(config) {
    const url = config && config.url ? config.url : "";
    if (!url) return false;
    return url === this.refreshPath || url.endsWith(this.refreshPath);
  },

  _isLoginRequest(config) {
    const url = config && config.url ? config.url : "";
    if (!url) return false;
    const loginPath = this._joinPath(this.authPathPrefix, "login");
    return url === loginPath || url.endsWith(loginPath);
  },

  _refreshAuth() {
    if (!this._refreshPromise) {
      this._refreshPromise = this.authAxios
        .get(this.refreshPath, { __skipAuthRefresh: true })
        .then(response => {
          const data = response && response.data ? response.data : null;
          if (data && data.accessTokenExpiresAt && this.store) {
            const JwtService = require("./jwt.service").default;
            JwtService.storeExpiresAt(data.accessTokenExpiresAt);
          }
          return response;
        })
        .finally(() => {
          this._refreshPromise = null;
        });
    }
    return this._refreshPromise;
  },

  _redirectForStatus(status) {
    const override = this.httpRedirects && this.httpRedirects[status] ? this.httpRedirects[status] : null;
    if (override) {
      this._goTo(override);
      return;
    }
    if (status === 403) {
      this._goTo("/accessDenied");
      return;
    }
    if (status === 401) {
      this._goTo("/login");
    }
  },

  _goTo(path) {
    if (!path || typeof path !== "string") return;
    const targetPath = path.startsWith("/") ? path : `/${path}`;
    const targetHash = `#${targetPath}`;
    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    }
  },

  _joinPath(prefix, suffix) {
    const p = (prefix || "").replace(/\/+$/, "");
    const s = (suffix || "").replace(/^\/+/, "");
    return `${p}/${s}`;
  },

  _axiosFor(resource) {
    if (!resource || typeof resource !== "string") return this.apiAxios;

    if (this.authPathPrefix && resource.startsWith(this.authPathPrefix)) {
      return this.authAxios;
    }

    if (this.authBaseUrl && resource.startsWith(this.authBaseUrl)) {
      return this.authAxios;
    }

    return this.apiAxios;
  },

  /**
   *
   * @param {*} property
   * @param {*} object
   */
  getRelatedObject(property, object) {
    return this.get(this.getLinkUrl(property, object));
  },

  /**
   * Convenience method to retrieve the self link href within an object
   * @param {} type
   * @param {*} obj
   */
  getSelfUrl(type, obj) {
    return this.baseUrl + "/api/" + type + "/" + obj.id;
  },

  /**
   * Convenience method to retrieve the link href url within an object
   * @param {*} type
   * @param {*} obj
   */
  getLinkUrl(type, obj) {
    let href = obj && obj._links && obj._links[type] && obj._links[type].href ? obj._links[type].href : null;
    if (href != null) {
      href = this.normalizeLinkUrl(href);
    }
    return href;
  },

  createError(message) {
    let apiError = new ApiError();
    apiError.message = message;
    return apiError;
  },

  /**
   * Utility function used by forms to retrieve an error array that can be used to format server-side validation errors
   * @param {*} error
   */
  getErrorsFromResponse(error) {
    let status = error && error.response && error.response.status ? error.response.status : null;
    let apiErrors = new ApiError();
    if (typeof error == "string") {
      apiErrors.message = error;
    } else if (error && error.response && error.response.data) {
      let errorMessage =
        this.getInvalidCredentialsMessage(error) ||
        error.response.data.errorMessage ||
        error.response.data.detail ||
        error.response.data.title;
      apiErrors.httpStatusCode = status;
      apiErrors.code = this.getErrorResponseProblemCode(error);
      if (status == 400) {
        if (errorMessage && errorMessage.startsWith("Validation failed")) {
          apiErrors.message = "Validation errors occurred while saving.  Please resolve.";
          error.response.data.errors.forEach(e => {
            apiErrors.addValidationError(e.property, e.message);
          });
        } else if (
          error.response.data.properties &&
          error.response.data.properties.errors &&
          error.response.data.properties.errors.fields
        ) {
          apiErrors.message = error.response.data.detail || "Validation errors occurred while saving.  Please resolve.";
          Object.entries(error.response.data.properties.errors.fields).forEach(([field, message]) => {
            apiErrors.addValidationError(field, message);
          });
        } else if (error.response.data.errors && error.response.data.errors.fields) {
          apiErrors.message = error.response.data.detail || "Validation errors occurred while saving.  Please resolve.";
          Object.entries(error.response.data.errors.fields).forEach(([field, message]) => {
            apiErrors.addValidationError(field, message);
          });
        }
      } else if (status == 409) {
        if (
          error.response.data.properties &&
          error.response.data.properties.errors &&
          error.response.data.properties.errors.fields
        ) {
          apiErrors.message =
            error.response.data.detail || "There is a constraint or dependency that prevented your request";
          Object.entries(error.response.data.properties.errors.fields).forEach(([field, message]) => {
            apiErrors.addValidationError(field, message);
          });
        } else if (error.response.data.errors && error.response.data.errors.fields) {
          apiErrors.message =
            error.response.data.detail || "There is a constraint or dependency that prevented your request";
          Object.entries(error.response.data.errors.fields).forEach(([field, message]) => {
            apiErrors.addValidationError(field, message);
          });
        } else if (error.response.data.errors && error.response.data.errors.length > 0) {
          error.response.data.errors.forEach(e => {
            apiErrors.addValidationError(e.property, e.message);
          });
        } else {
          apiErrors.message = "There is a constraint or dependency that prevented your request";
          apiErrors.addValidationError("Constraint", error.response.data.detail);
        }
      } else if (status == 404) {
        apiErrors.message = "Resource not found";
      } else if (status == 409 || status == 413) {
        apiErrors.message = errorMessage;
      } else if (status == 500) {
        apiErrors.message = error.response.data.detail;
      } else if (errorMessage) {
        apiErrors.message = errorMessage;
      } else {
        apiErrors.message = "An unkown error occurred. Please try again";
      }
    }

    if (
      error &&
      error.response &&
      error.response.status &&
      this.httpRedirects &&
      this.httpRedirects[error.response.status]
    ) {
      // this.$router.push(this.httpRedirects[error.response.status]); this doesn't work.
    }
    if (
      !apiErrors.message &&
      error &&
      error.message &&
      (!apiErrors.validationErrors || Object.keys(apiErrors.validationErrors).length === 0)
    ) {
      apiErrors.message = error.message;
    }

    return apiErrors;
  },

  getErrorResponseData(errorResponse) {
    return errorResponse && errorResponse.response ? errorResponse.response.data : null;
  },

  getErrorResponseStatus(errorResponse) {
    const response = errorResponse && errorResponse.response ? errorResponse.response : null;
    const data = response ? response.data : null;

    if (data && data.status) {
      return data.status;
    }

    return response ? response.status : null;
  },

  getErrorResponseProblemCode(errorResponse) {
    const data = this.getErrorResponseData(errorResponse);
    if (!data) {
      return "";
    }

    return data.properties?.code ?? data.code ?? "";
  },

  getErrorResponseProperty(errorResponse, propertyName) {
    const data = this.getErrorResponseData(errorResponse);
    if (!data) {
      return null;
    }

    return data.properties?.[propertyName] ?? data[propertyName] ?? null;
  },

  getInvalidCredentialsMessage(errorResponse) {
    if (this.getErrorResponseProblemCode(errorResponse) !== CREDENTIALS_INVALID_CODE) {
      return "";
    }

    const remainingAttempts = Number(this.getErrorResponseProperty(errorResponse, "remainingAttempts"));
    if (remainingAttempts <= 0) {
      return "";
    }

    return `Invalid credentials. Your account will be locked after ${remainingAttempts} more failed attempts.`;
  },

  isUnauthorized(errorResponse) {
    const status = this.getErrorResponseStatus(errorResponse);
    return status == 401 && !this.isCredentialsExpired(errorResponse);
  },

  isForbidden(errorResponse) {
    return (
      errorResponse &&
      errorResponse.response &&
      errorResponse.response.data &&
      errorResponse.response.data.status == 403
    );
  },

  isCredentialsExpired(errorResponse) {
    const status = this.getErrorResponseStatus(errorResponse);
    const code = this.getErrorResponseProblemCode(errorResponse);

    return status == 412 || (status == 401 && code == CREDENTIALS_EXPIRED_CODE);
  },

  getCredentialsExpirationKey(errorResponse) {
    if (!this.isCredentialsExpired(errorResponse)) {
      return "";
    }

    const credentialsExpiredKey = this.getErrorResponseProperty(errorResponse, "credentialsExpiredKey");
    if (credentialsExpiredKey) {
      return credentialsExpiredKey;
    }

    const data = this.getErrorResponseData(errorResponse);
    return data && data.errorMessage ? data.errorMessage : "";
  },

  isPayloadTooLarge(errorResponse) {
    return (
      errorResponse &&
      errorResponse.response &&
      errorResponse.response.data &&
      errorResponse.response.data.status == 413
    );
  },

  isLocked(errorResponse) {
    return this.getErrorResponseStatus(errorResponse) == 423;
  },

  getLockedMessage(errorResponse, client) {
    const data = this.getErrorResponseData(errorResponse);
    const lockUntil = data?.lockUntil;

    if (lockUntil) {
      const formattedLockUntil = UtilService.formatDateClient(lockUntil, "MM/DD/YYYY hh:mm A", client);

      return `Your account is locked until ${formattedLockUntil}.`;
    }

    return data.errorMessage || data.detail || data.title;
  },

  async postAndReturnTypedArray(resource) {
    const axiosInstance = this._axiosFor(resource);
    const response = await axiosInstance.post(resource, {
      responseType: "arraybuffer"
    });
    const typedArray = new Uint8Array(response.data);
    return typedArray;
  },

  async getAndReturnTypedArray(resource) {
    const axiosInstance = this._axiosFor(resource);
    const response = await axiosInstance.get(resource, {
      responseType: "arraybuffer"
    });
    const typedArray = new Uint8Array(response.data);
    return typedArray;
  },

  /**
   * Set the default HTTP request headers
   */
  setHeaders() {
    this._setHeadersOnApi(this.apiAxios);
  },

  _setHeadersOnApi(axiosInstance) {
    if (!axiosInstance) return;

    // Cookie auth, plus locale header for monolith API.
    axiosInstance.defaults.headers.common["Accept"] = "application/json";

    const store = this.store;
    const locale = store && store.getters ? store.getters.selectedLocale : null;
    if (locale && locale.code) {
      axiosInstance.defaults.headers.common["Accept-Language"] = locale.code;
    } else {
      delete axiosInstance.defaults.headers.common["Accept-Language"];
    }
  },

  query(resource, params) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.get(resource, params);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @returns {*}
   */
  get(resource) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.get(resource);
  },

  getWithConfig(resource, config) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.get(resource, config);
  },

  getWithCaptcha(resource, action) {
    this.setHeaders();
    return Vue.prototype.$recaptchaLoaded().then(() => {
      return Vue.prototype.$recaptcha(action ? action : "generic").then(token => {
        let finalUrl = resource.includes("?")
          ? resource + "&g-recaptcha-response=" + token
          : resource + "?g-recaptcha-response=" + token;
        const axiosInstance = this._axiosFor(finalUrl);
        return axiosInstance.get(finalUrl);
      });
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params, config) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.post(resource, params, config);
  },

  postWithCaptcha(resource, params, config, action) {
    this.setHeaders();

    return Vue.prototype.$recaptchaLoaded().then(() => {
      return Vue.prototype.$recaptcha(action ? action : "generic").then(token => {
        const finalUrl = resource + (resource.includes("?") ? "&" : "?") + "g-recaptcha-response=" + token;
        const axiosInstance = this._axiosFor(finalUrl);
        return axiosInstance.post(finalUrl, params, config);
      });
    });
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, params) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.put(resource, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.put(resource, params);
  },

  /**
   * Send the PATCH HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  patch(resource, params) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.patch(resource, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    this.setHeaders();
    const axiosInstance = this._axiosFor(resource);
    return axiosInstance.delete(resource);
  }
};
export default ApiService;

class ApiError {
  httpStatusCode = 0;
  message = "";
  validationErrors = {};
  code = "";

  constructor(httpStatusCode = 0, message = "", validationErrors = {}, code = "") {
    this.httpStatusCode = httpStatusCode;
    this.message = message;
    this.validationErrors = validationErrors;
    this.code = code;
  }

  getValidationError(key) {
    return this.validationErrors[key];
  }
  clearValidationError(key) {
    this.validationErrors[key] = undefined;
  }
  addValidationError(key, value) {
    if (this.validationErrors[key]) {
      this.validationErrors[key] += `\n${value}`;
    } else {
      this.validationErrors[key] = value;
    }
  }
  isBadRequest() {
    return this.httpStatusCode == 400;
  }
  isUnauthorized() {
    return this.httpStatusCode == 401 && !this.isCredentialsExpired();
  }
  isForbidden() {
    return this.httpStatusCode == 403;
  }
  isNotFound() {
    return this.httpStatusCode == 404;
  }
  isNotAllowed() {
    return this.httpStatusCode == 405;
  }
  isRequestTimeOut() {
    return this.httpStatusCode == 408;
  }
  isCredentialsExpired() {
    return this.httpStatusCode == 412 || (this.httpStatusCode == 401 && this.code == CREDENTIALS_EXPIRED_CODE);
  }
  isPayloadTooLarge() {
    return this.httpStatusCode == 413;
  }
  isLocked() {
    return this.httpStatusCode == 423;
  }
}
